import AccountCard from "features/dashboard/Accounts/AccountCard";
import React, { useMemo } from "react";
import {
  ACCOUNT_CARD_HEIGHT,
  ACCOUNT_CARD_WIDTH,
  MOBILE_ACCOUNT_CARD_HEIGHT,
  MOBILE_ACCOUNT_CARD_WIDTH,
} from "features/dashboard/common/constants";
import VirtualCardsList from "common/Lists/VirtualCardsList";
import { MOBILE_SEARCH, PORTFOLIO_ACCOUNTS_PATH } from "common/constants";
import {
  ClientDataWithId,
  ConsolidatedAccountData,
  ConsolidatedAccountDataWithId,
  LanguageNameSpaces,
} from "@shared/types";
import { EmptyWrapper, SectionTitle } from "features/dashboard/common/styles";
import useGetVirtualListCardDimensions from "utils/hooks/useGetVirtualListCardDimensions";
import { VIRTUAL_CARDS_LIST_PADDING } from "@shared/common/constants";
import AccountsEmpty from "features/portfolio/Accounts/AccountsEmpty";
import { sharedLib } from "@shared/services/shared.service";
import useMedia from "utils/hooks/useMedia.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";

interface AccountCardsProps {
  accountsData: ConsolidatedAccountData[];
}

const AccountCards = ({ accountsData }: AccountCardsProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { isLoading: getPortfolioDataLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const {
    data: accounts,
    isLoading: getAccountsListLoading,
    isUninitialized: getAccountsListUninitialized,
  } = sharedLib.sharedApi.configuration.useGetClientsListQuery();
  const { data: accountsAmounts, isLoading: accountsAmountsLoading } =
    sharedLib.sharedApi.portfolio.useGetPortfolioAmountQuery();
  const { onCreateAccountClick, whopModal } = useCreateAccountClick();

  const sortedAccountsByTotal = useMemo(() => {
    if (accountsData.length) {
      return accountsData
        .slice()
        .sort((a, b) => b.total_in_usd - a.total_in_usd);
    } else if (accounts && accountsAmounts) {
      return accounts
        .filter((accountData) => accountData.is_valid)
        .sort(
          (a, b) => accountsAmounts[b.client_id] - accountsAmounts[a.client_id]
        );
    }
  }, [accountsData, accountsAmounts, accounts]);

  const sortedAccountsWithId:
    | (ConsolidatedAccountDataWithId | ClientDataWithId)[]
    | undefined = useMemo(() => {
    return sortedAccountsByTotal?.map((account) => {
      return { ...account, id: account.client_id };
    });
  }, [sortedAccountsByTotal]);

  const { cardHeight, cardWidth } = useGetVirtualListCardDimensions(
    ACCOUNT_CARD_HEIGHT,
    MOBILE_ACCOUNT_CARD_HEIGHT,
    ACCOUNT_CARD_WIDTH,
    MOBILE_ACCOUNT_CARD_WIDTH
  );

  const title = <SectionTitle>{t("accounts.title")}</SectionTitle>;
  const noAccounts =
    !getAccountsListUninitialized &&
    sortedAccountsWithId &&
    !sortedAccountsWithId.length;

  const empty = (
    <>
      <div
        style={{
          margin: `0 ${VIRTUAL_CARDS_LIST_PADDING}px 8px ${VIRTUAL_CARDS_LIST_PADDING}px`,
        }}
      >
        {title}
      </div>
      <EmptyWrapper $cardHeight={cardHeight}>
        <AccountsEmpty
          buttonOnClick={onCreateAccountClick}
          iconWidth={isMobile ? 100 : 140}
        />
      </EmptyWrapper>
    </>
  );

  return (
    <>
      {noAccounts ? (
        empty
      ) : (
        <VirtualCardsList
          cardsData={sortedAccountsWithId}
          loading={
            getPortfolioDataLoading &&
            (getAccountsListLoading || accountsAmountsLoading)
          }
          cardHeight={cardHeight}
          cardWidth={cardWidth}
          CardComponent={AccountCardComponent}
          title={title}
          seeAllLink={PORTFOLIO_ACCOUNTS_PATH}
        />
      )}
      {whopModal}
    </>
  );
};

export default React.memo(AccountCards);

interface AccountCardComponentProps {
  data: ConsolidatedAccountDataWithId | ClientDataWithId;
  extraData?: unknown;
  index: number;
}

const AccountCardComponent = ({ data }: AccountCardComponentProps) => {
  return <AccountCard accountPortfolioData={data} />;
};
